import { db } from '@core/db/db'
import { META_PENDING_PRICES } from '@core/db/schema'
import {
  EDITABLE_FIELDS,
  matchesSentEdit,
  mergePendingEdit,
  type PendingProductEdit,
  type ProductEditFields,
  type SentEdit
} from './pendingEditLogic'

/**
 * Очередь правок товаров из админки (ARCH-TASK-60.3, обобщена в 64a).
 *
 * Правка (цена и/или минимальный остаток) должна доехать до облака и не быть
 * затёртой дельтой каталога при обрыве связи. Пока позиция в очереди,
 * `pullCatalog` не перезаписывает локальные значения облачными.
 *
 * Ключ meta оставлен прежним (`META_PENDING_PRICES`): старые очереди с одной
 * ценой читаются без миграции. Чистая логика — в `pendingEditLogic`.
 */
export type { PendingProductEdit, ProductEditFields, SentEdit }


/**
 * Сколько попыток даём перед снятием позиции (≈1 час при цикле 60 с).
 * Постоянные ошибки (400/403/404) снимаются сразу и сюда не доходят.
 */
export const MAX_EDIT_ATTEMPTS = 60

function sanitize(item: unknown): PendingProductEdit | null {
  if (typeof item !== 'object' || item == null) return null
  const candidate = item as Partial<PendingProductEdit>
  if (typeof candidate.cloudId !== 'number') return null
  const edit: PendingProductEdit = {
    cloudId: candidate.cloudId,
    at: typeof candidate.at === 'number' ? candidate.at : Date.now(),
    attempts: typeof candidate.attempts === 'number' ? candidate.attempts : 0
  }
  if (typeof candidate.price === 'number') edit.price = candidate.price
  if (candidate.minStock === null || typeof candidate.minStock === 'number') {
    edit.minStock = candidate.minStock as number | null
  }
  if (typeof candidate.name === 'string') edit.name = candidate.name
  if (typeof candidate.barcode === 'string') edit.barcode = candidate.barcode
  if (typeof candidate.category === 'string') edit.category = candidate.category
  if (typeof candidate.categoryParent === 'string') {
    edit.categoryParent = candidate.categoryParent
  }
  if (typeof candidate.requiresMarking === 'boolean') {
    edit.requiresMarking = candidate.requiresMarking
  }
  const hasAny = EDITABLE_FIELDS.some((field) => edit[field] !== undefined)
  return hasAny ? edit : null
}

async function readQueue(): Promise<PendingProductEdit[]> {
  const entry = await db.meta.get(META_PENDING_PRICES)
  const value = entry?.value
  if (!Array.isArray(value)) return []
  return value.map(sanitize).filter((item): item is PendingProductEdit => item != null)
}

async function writeQueue(list: PendingProductEdit[]): Promise<void> {
  await db.meta.put({ key: META_PENDING_PRICES, value: list })
}

/** Прочитать очередь (битые элементы игнорируются). */
export async function listPendingEdits(): Promise<PendingProductEdit[]> {
  return readQueue()
}

/**
 * Поставить/обновить правку в очереди (по одному товару — последняя правка).
 * Поля мержатся: правка цены не стирает минимальный остаток и наоборот.
 */
export async function enqueuePendingEdit(
  cloudId: number,
  patch: ProductEditFields
): Promise<void> {
  await db.transaction('rw', db.meta, async () => {
    const list = await readQueue()
    const existing = list.find((item) => item.cloudId === cloudId)
    const merged = mergePendingEdit(existing, cloudId, patch, Date.now())
    await writeQueue([...list.filter((item) => item.cloudId !== cloudId), merged])
  })
}

/**
 * Убрать отправленные позиции: только при совпадении полей.
 * Если за время отправки значение поменяли ещё раз, новая запись остаётся.
 */
export async function removePendingEdits(sent: SentEdit[]): Promise<void> {
  if (sent.length === 0) return
  await db.transaction('rw', db.meta, async () => {
    const list = await readQueue()
    await writeQueue(list.filter((entry) => !sent.some((done) => matchesSentEdit(entry, done))))
  })
}

/**
 * Учесть неудачную попытку отправки.
 * @returns true, если позиция снята с очереди (лимит попыток исчерпан).
 */
export async function failPendingEdit(sent: SentEdit): Promise<boolean> {
  let dropped = false
  await db.transaction('rw', db.meta, async () => {
    const list = await readQueue()
    const next: PendingProductEdit[] = []
    for (const entry of list) {
      if (!matchesSentEdit(entry, sent)) {
        next.push(entry)
        continue
      }
      const attempts = entry.attempts + 1
      if (attempts >= MAX_EDIT_ATTEMPTS) {
        dropped = true
        continue
      }
      next.push({ ...entry, attempts })
    }
    await writeQueue(next)
  })
  return dropped
}

/** Карта cloudId → неотправленная правка для пулла каталога. */
export async function pendingEditMap(): Promise<Map<number, ProductEditFields>> {
  const list = await readQueue()
  return new Map(
    list.map((item) => [item.cloudId, { price: item.price, minStock: item.minStock }])
  )
}

/** Есть ли неотправленная правка по товару. */
export async function hasPendingEdit(cloudId: number): Promise<boolean> {
  const list = await readQueue()
  return list.some((item) => item.cloudId === cloudId)
}
